import {
  BeneficiaryCreateRequest,
  BeneficiaryCreateResult,
  CardCreateRequest,
  CardCreateResult,
  CopCheckRequest,
  CopCheckResult,
  ExchangeAcceptResult,
  ExchangeQuoteRequest,
  ExchangeQuoteResult,
  InternationalBeneficiaryCreateRequest,
  InternationalPaymentPrecheckResult,
  InternationalPaymentRequest,
  InternationalPaymentResult,
  NominatedAccountConnectResult,
  NominatedAccountProcessResult,
  PaymentInitiationRequest,
  PaymentInitiationResult,
  PaymentRequestCreateRequest,
  PaymentRequestCreateResult,
  SavingsAccountCloseRequest,
  SavingsAccountCloseResult,
  SavingsAccountCreateRequest,
  SavingsAccountCreateResult,
  SavingsInternalTransferRequest,
  SavingsInternalTransferResult,
  SavingsMaturitySetRequest,
  SavingsMaturitySetResult,
  SavingsWithdrawRequest,
  SavingsWithdrawResult,
  WidgetAccount,
  WidgetApiOperations,
  WidgetBeneficiary,
  WidgetBeneficiaryRequirements,
  WidgetBulkPayment,
  WidgetBulkPaymentDetail,
  WidgetCard,
  WidgetCardLimit,
  WidgetNominatedAccount,
  WidgetNominatedAccountInstitution,
  WidgetProfile,
  WidgetDocumentDownloadResult,
  WidgetDocumentFormat,
  WidgetDocumentRequest,
  WidgetHostHandlers,
  WidgetNotification,
  WidgetPage,
  WidgetPaymentRequest,
  WidgetPaymentScheme,
  WidgetSavingsAccount,
  WidgetSavingsProduct,
  WidgetScheduledPayment,
  WidgetStoredDocument,
  WidgetTransaction,
  WidgetTransactionDetail,
  WidgetTransferReason,
  derivePaymentInitiationStatus,
  invalidScheduledAtReason,
} from '@finxone-platform/shared/app-sdk';
import {
  AccountStatusType,
  CardType,
  DocumentActionCategoryType,
  DocumentActionType,
  DownloadDocumentTypeKey,
  MaturityDecision,
  MaturityDecisionRequest,
  PaymentRailOptions,
  TransactionType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { AccountService } from '../../../../lib/services/account-service/account-service.service';
import { CardManagementService } from '../../../../lib/services/card-management-service/card-management-service';
import {
} from '../../../../lib/services/card-secret-reveal/card-secret-reveal.types';
import { DocumentService } from '../../../../lib/services/document-service/document.service';
import { DownloadDocumentService } from '../../../../lib/services/download-document/download-document.service';
import { NotificationService } from '../../../../lib/services/notification-service/notification-service.service';
import { PasswordService } from '../../../../lib/services/password-service/password-service.service';
import { ExchangeService } from '../../../../lib/services/exchange-service/exchange-service.service';
import { SavingsAccountService } from '../../../../lib/services/savings-account-service/savings-account.service';
import { TransactionService } from '../../../../lib/services/transaction-service/transaction.service';
import { TransferMoneyRequest } from '../../../../lib/services/account-service/account.type';
import { SetSelectedBeneficiary } from '../../../../lib/actions/beneficiary.action';
import {
  UpdateCurrentAccountId,
  UpdateCurrentMultiCurrencyCode,
} from '../../../../lib/actions/account.action';
import { UpdateCurrentCardDetail } from '../../../../lib/actions/card.actions';
import { UpdateActiveSavingsAccountId } from '../../../../lib/actions/savings-account.action';
import { AccountState } from '../../../../lib/state/account.state';
import { CardState, CardStateModel } from '../../../../lib/state/card.state';
import {
} from '../../../../lib/services/overlay-confirmation-service/confirmation-declined-error';
import {
  CONFIRMATION_GATED_OPERATIONS,
  ConfirmationOutcome,
  ConfirmationRunner,
  createModalOutletConfirmationRunner,
} from './custom-widget-confirmation';
import { CustomWidgetAuthBridge } from './custom-widget-auth';
import { MetadataService } from '../../../../lib/services/metadata-service/metadata-service.service';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { buildCustomWidgetLookupOperations } from './custom-widget-lookup-operations';
import { buildCustomWidgetOnboardingBridge } from './custom-widget-onboarding';
import {
  TransactionWireItem,
  toAddBeneficiaryRequest,
  toAddInternationalBeneficiaryRequest,
  toCopCheckResult,
  toCreateSavingsAccountRequest,
  toExchangeAcceptResult,
  toExchangeQuotePayload,
  toExchangeQuoteResult,
  toInternationalPaymentPrecheckResult,
  toInternationalPaymentResult,
  toInternationalTransferMoneyRequest,
  toPayee,
  toPaymentRequestorRequest,
  toSavingsAccountCreateResult,
  toWidgetAccount,
  toWidgetBeneficiary,
  toWidgetBeneficiaryRequirements,
  toWidgetBulkPayment,
  toWidgetBulkPaymentSummary,
  toWidgetBulkPaymentTransfer,
  toWidgetCard,
  toWidgetCardLimit,
  toWidgetPaymentRequest,
  toWidgetPaymentRequestDetail,
  toWidgetPaymentScheme,
  toProfileUpdatePayload,
  toWidgetNominatedAccount,
  toWidgetSavingsAccount,
  toWidgetSavingsAccountRequest,
  toWidgetSavingsProduct,
  toWidgetStoredDocument,
  toWidgetScheduledPayment,
  toWidgetTransaction,
  toWidgetTransactionDetail,
  toWidgetTransferReason,
} from './custom-widget-contract-mappers';

/**
 * The product kinds `savings.create` accepts. The same list the BFF enforces
 * (`APP_SAVINGS_ACCOUNT_TYPES`) — one contract, so both hosts must accept the same
 * kinds or the same widget behaves differently on each transport.
 */
const SAVINGS_ACCOUNT_TYPES: readonly SavingsAccountCreateRequest['accountType'][] = [
  'easy-access',
  'fixed-term',
  'notice',
];

/**
 * Named operations callable by custom widgets via sdk.api.call(). Each is a
 * one-shot wrapper over the existing domain services with the response mapped
 * onto its app-sdk contract shape (contracts.ts WidgetApiOperations):
 *
 * - 'accounts.list'           { page?, size? } -> GET /account/list, mapped to
 *                             WidgetPage<WidgetAccount>.
 * - 'transactions.list'       { accountId?, page?, size?, from?, to?, sort? } ->
 *                             GET /account/:id/transactions (type ALL);
 *                             accountId defaults to the currently selected
 *                             account, from/to are ISO date bounds and sort
 *                             orders by transaction date (all three optional —
 *                             omitted, the request is exactly what it was
 *                             before they existed). No direction filter: the
 *                             native seam hardcodes the type per endpoint, so
 *                             there is nothing to thread one into. Mapped to
 *                             WidgetPage<WidgetTransaction>.
 * - 'payments.initiate'       PaymentInitiationRequest -> POST /payments
 *                             (TransferMoneyDTO: beneficiaryId -> bid,
 *                             reference -> paymentReference, scheduledAt ->
 *                             schedulePaymentDate after the SDK's shared
 *                             date rule, since the endpoint itself validates
 *                             the field as a bare optional string; the backend
 *                             requires a non-empty transferReasonId wherever
 *                             transfer reasons are configured). Result is
 *                             PaymentInitiationResult with transactionId from
 *                             the response id and `status` from
 *                             derivePaymentInitiationStatus — the endpoint
 *                             answers with the same body for an executed
 *                             transfer, a future-dated job (`scheduledAt`) and
 *                             a cancellation-window job (`expiryReversalAt`).
 * - 'beneficiaries.cop.check' CopCheckRequest -> POST /beneficiaries/check-payee
 *                             (accountType defaults 'personal'), mapped to
 *                             CopCheckResult via the matchStatusCode table in
 *                             custom-widget-contract-mappers.ts.
 * - 'payments.reasons.list'   { accountId?, country?, category? } -> GET
 *                             /payments/:accountId/transfer-reasons/:country;
 *                             accountId defaults to the selected account and
 *                             country to 'GB' (native parity:
 *                             payment-reason-widget).
 * - 'beneficiaries.requirements' { currency, country?, scheme? } -> GET
 *                             /beneficiaries/beneficiary-bank-account-requirements,
 *                             flattened onto WidgetBeneficiaryRequirements.
 * - 'beneficiaries.create'    BeneficiaryCreateRequest -> POST /beneficiaries
 *                             (AddBeneficiaryRequest); the confirmation token
 *                             from the runner rides the `confirmation` header.
 * - 'cards.list'              { page?, size?, name?, type?, status? } -> GET
 *                             /cards, mapped to WidgetCard[] (no sensitive
 *                             fields; PAN/CVV/PIN stay behind the secure
 *                             endpoints, which are not exposed here). The array
 *                             is the requested page; with everything omitted
 *                             the call is byte-for-byte what it always was
 *                             (page 1, size 20).
 * - 'cards.freeze'/'cards.unfreeze' { cardId } -> POST /cards/freeze|unfreeze,
 *                             resolving { status } from the response. The
 *                             cards data topic reflects Ngxs state, so it
 *                             refreshes on the next native fetch, not on this
 *                             call.
 * - 'transactions.detail'     { transactionId, type, accountId? } -> GET
 *                             /payments/transactions?id=&type=&accountId=
 *                             (TransactionService.getTransactionDetailById —
 *                             the same call the native elaborated view makes).
 *                             `type` must be the payment direction
 *                             ('incoming'|'outgoing'; scheduled/exchange
 *                             details live behind separate native endpoints);
 *                             accountId defaults to the selected account.
 * - 'payments.scheduled.list' { page?, size? } -> GET
 *                             /payments/open-for-reversal
 *                             (getCancellablePayments): payments still open
 *                             for cancellation across the user's accounts —
 *                             the endpoint takes no accountId filter.
 * - 'paymentRequests.list'    { page?, size?, status?, query? } -> GET
 *                             /payments/requests, mapped to
 *                             WidgetPage<WidgetPaymentRequest>.
 * - 'paymentRequests.get'     { paymentRequestId } -> GET
 *                             /payments/request/:id
 *                             (getPaymentRequestDetailsById), mapped onto the
 *                             same WidgetPaymentRequest shape.
 * - 'cards.limits.get'        { cardId } -> GET /cards/:cardId/limits, string
 *                             amounts parsed onto WidgetCardLimit[].
 * - 'savings.accounts.list'   { page?, size? } -> GET /savings/ (active;
 *                             page/size default to 1/50, the values this
 *                             operation always sent), mapped to
 *                             WidgetSavingsAccount[] — the one-shot equivalent
 *                             of the savingsAccounts topic.
 * - 'beneficiaries.list'      { page?, size?, name?, currency?, country?,
 *                             accountNumber? } -> GET /beneficiaries
 *                             (BeneficiarySearchParams: name/accountNumber are
 *                             substring matches, currency/country exact;
 *                             omitted paging leaves the backend defaults,
 *                             page 1 size 20). The one-shot, parameterised
 *                             counterpart of the beneficiaries topic, reading
 *                             the same platform table — rows are synced from
 *                             the provider out-of-band. Mapped to
 *                             WidgetPage<WidgetBeneficiary> via the topic's own
 *                             toWidgetBeneficiary.
 * - 'savings.products.list'   { category? } -> GET /savings/available-products
 *                             ('renewal' adds ?category=renewal; 'standard',
 *                             the default, sends no param — the
 *                             new-account catalogue), mapped to
 *                             WidgetSavingsProduct[].
 * - 'savings.products.listForCustomer'
 *                             {} -> GET /savings/customer-available-products,
 *                             the org-scoped catalogue (organisation read from
 *                             the token, so nothing is passed), mapped through
 *                             the same toWidgetSavingsProduct. Returns the same
 *                             set as 'savings.products.list' until a provider
 *                             implements per-organisation eligibility — every
 *                             ISavings implementation currently discards the
 *                             organisation id.
 * - 'savings.create'          SavingsAccountCreateRequest -> POST /savings
 *                             (CreateSavingsAccountRequest). The response
 *                             nests under `data` and is an account *request*:
 *                             accountId only appears on 'approved', otherwise
 *                             requestId identifies the pending application.
 *                             The wire response's sumsubAccessToken (a KYC
 *                             credential) is never forwarded.
 * - 'savings.close'           { savingsAccountId, closureReason? } -> POST
 *                             /savings/:accountId/close. No response body, so
 *                             the result echoes the account id. The balance
 *                             goes to the customer's nominated account — the
 *                             endpoint takes no destination.
 * - 'savings.internalTransfer' { fromSavingsAccountId, toSavingsAccountId,
 *                             amount } -> POST
 *                             /savings/:accountId/internal-transfer, whose
 *                             ConfirmationGuard wants the makePayment claim.
 *                             Confirmation-gated; no token is attached
 *                             (initiateInternalTransfer has no accessToken
 *                             parameter — native parity with
 *                             initiateSavingsInternalTransfer, which sequences
 *                             the call after the confirmation).
 * - 'savings.withdraw'        { savingsAccountId, amount, description? } ->
 *                             POST /savings/:accountId/withdraw, to the
 *                             customer's nominated account. The account is
 *                             read first (GET /savings/:id) for the two things
 *                             the native util takes from page state: the
 *                             availableBalance the amount is validated
 *                             against, and the accountType the outcome is
 *                             derived from — the response body is a
 *                             provider-shaped throwaway the native flow never
 *                             reads. A notice account answers
 *                             'notice-submitted' (nothing moved yet); anything
 *                             else 'withdrawn'. Not gated: the native flow
 *                             runs no confirmation and the route carries no
 *                             guard. The native full-balance-on-closure-
 *                             enabled diversion into the closure journey is
 *                             deliberately not reproduced — see the contract.
 * - 'savings.accounts.rename' { savingsAccountId, accountName } -> PATCH
 *                             /savings/:accountId. No response body, so the
 *                             echoed request is the result.
 * - 'savings.requests.list'   { status? } -> GET /savings/account-requests
 *                             (no status = every request, whatever its state),
 *                             mapped to WidgetSavingsAccountRequest[].
 * - 'savings.maturity.set'    { savingsAccountId, decision, productCode?,
 *                             reinvestAmount? } -> POST
 *                             /savings/:accountId/maturity-decision, the one
 *                             endpoint behind all three native maturity CTA
 *                             flows: 'renew'/'withdraw' send the legacy
 *                             { decision, productCode? } shape and
 *                             'partial-reinvest' sends advanced CUSTOM
 *                             actions with the two legs computed from the
 *                             account's current balance (read fresh, as the
 *                             native flow reads it from state). The response
 *                             carries an empty data object, so the result
 *                             echoes the instruction.
 * - 'exchange.quote'          ExchangeQuoteRequest -> POST /exchanges/quote
 *                             (amount stringified, fixedSide -> fixedType).
 *                             Resolves quoteId + rate + expiresAt so a widget
 *                             can run a countdown; re-quote by calling again.
 * - 'exchange.accept'         { quoteId } -> POST /exchanges/transactions,
 *                             mapped to ExchangeAcceptResult. Not gated: the
 *                             native acceptQuote flow runs no confirmation and
 *                             the endpoint has no ConfirmationGuard.
 * - 'payments.scheduled.cancel' { paymentId } -> DELETE
 *                             /payments/payment-reversal/:jobId
 *                             (removeScheduledPayment; paymentId is the
 *                             payments.scheduled.list row id, which is the
 *                             reversal job id). Bulk cancellation
 *                             (cancelAllPayment) is a bulk-payments master
 *                             operation and is not exposed.
 * - 'paymentRequests.create'  PaymentRequestCreateRequest -> POST
 *                             /payments/request (makePaymentRequest): asks an
 *                             approver to fund a payment. Not to be confused
 *                             with generatePaymentRequest, which PATCHes
 *                             /payments/requests to approve/reject an existing
 *                             one and is not exposed. sourceAccountId defaults
 *                             to the selected account. PaymentsRequestDTO
 *                             requires a non-empty paymentReference and
 *                             transfer reason, so reference/transferReasonId
 *                             are rejected when missing, and the reason's
 *                             label (transferReasonIdText) is resolved from
 *                             the payments.reasons.list catalogue — an id the
 *                             catalogue does not contain is rejected too.
 * - 'cards.create'            { accountId, cardReferenceType } -> POST /cards
 *                             (addCard, the working native seam) with
 *                             { accountIds, type: 'virtual', cardReferenceType }.
 *                             cardReferenceType is the project's card-product
 *                             key, which this host cannot derive — a payload
 *                             without it is rejected. The response body is
 *                             discarded, so the result only echoes accountId —
 *                             re-run cards.list for the new card. Physical
 *                             cards are not exposed (delivery address
 *                             collection is native-only).
 * - 'beneficiaries.createInternational' InternationalBeneficiaryCreateRequest ->
 *                             the same POST /beneficiaries as
 *                             beneficiaries.create (there is no separate
 *                             international endpoint): the routing identifiers
 *                             are flat fields inside destinationIdentifier and
 *                             the address is its sibling. Confirmation-gated on
 *                             the same claim/flag as the domestic operation,
 *                             with the token attached — see the note in
 *                             custom-widget-confirmation.ts for why the
 *                             ADD_INTERNATIONAL_BENEFICIARY claim is not used.
 * - 'payments.precheckInternational' InternationalPaymentRequest -> POST
 *                             /payments with review:true
 *                             (transferMoneyInternational), the dry run the
 *                             native flow (internationPaymentPrecheck) uses to
 *                             learn feeAmount/totalAmount before letting the
 *                             customer continue. Not gated: nothing moves.
 * - 'payments.initiateInternational' InternationalPaymentRequest -> the same
 *                             POST /payments with review:false. Domestic and
 *                             international share the endpoint and the
 *                             TransferMoneyRequest body; `review` is the only
 *                             discriminator. Confirmation-gated (MAKE_PAYMENT /
 *                             smsAuthenticationEnabled); no token is attached
 *                             (native parity — transferMoneyInternational sends
 *                             no `confirmation` header).
 * - 'documents.download'      WidgetDocumentRequest -> DownloadDocumentService,
 *                             the same service the native download CTAs use
 *                             (blob fetch -> object URL -> anchor click, or a
 *                             URL handed to the Flutter host when the app runs
 *                             in a webview). The host performs the download; the
 *                             operation resolves { started: true } and never
 *                             returns bytes or a URL. Note the service swallows
 *                             HTTP failures, so 'started' means handed to the
 *                             browser, not delivered.
 * - 'bulkPayments.list'       { page?, size?, query? } -> GET /bulk-payments,
 *                             mapped to WidgetPage<WidgetBulkPayment> (row
 *                             totalAmount is a wire string; dates are
 *                             snake_case).
 * - 'bulkPayments.get'        { bulkPaymentId, page?, size?, query? } -> GET
 *                             /bulk-payments/:id?metadataInclude=summary. The
 *                             response is a page of transfer rows plus
 *                             meta.bulkPaymentsSummary; both land on
 *                             WidgetBulkPaymentDetail. The rows' embedded
 *                             beneficiary bank accounts and addresses are not
 *                             forwarded.
 * - 'bulkPayments.retry'      { bulkPaymentId, transferId } -> POST
 *                             /bulk-payments/:masterId/transfer-payments/:id/retry
 *                             (retryBulkPaymentTransfer needs both ids). Not
 *                             gated — the native row action fires straight from
 *                             the menu with no confirmation. The response body
 *                             is unusable, so the ids are echoed.
 *                             Submitting a batch (makeBulkPayment) is not
 *                             exposed: it is CSV-upload-driven and the protocol
 *                             has no file transfer.
 * - 'payments.schemes.list'   { country?, currency? } -> GET
 *                             /beneficiaries/payment-schemes, a bare
 *                             { label, value } array.
 * - 'onboarding.*'           the seven onboarding operations (flow,
 *                            requirements, verify.send, verify.confirm,
 *                            submitIndividual, submitBusiness, verifyIdentity)
 *                            are answered by the onboarding bridge rather than
 *                            this switch, because they are stateful as a group
 *                            and share that state with the onboardingStatus
 *                            topic. They go to org-metadata-service, not
 *                            account-service, and the OTP mechanics stay inside
 *                            MetadataService. See custom-widget-onboarding.ts.
 * - 'auth.*'                 status/login/logout are answered by the auth bridge
 *                            for the same reason in reverse: they are the
 *                            session, so the onboarding submit's `onSuccess`
 *                            borrows the very same sign-in and login-redirect
 *                            functions rather than owning a second copy. Session
 *                            tokens never cross the port. See
 *                            custom-widget-auth.ts.
 *
 * Confirmation-gated operations (CONFIRMATION_GATED_OPERATIONS): when the
 * project settings require it, the widget's call suspends on the
 * ConfirmationRunner before the underlying request is dispatched; a declined
 * or unsupported confirmation rejects the call without any HTTP side effect.
 */
export function buildCustomWidgetApiOperations(
  store: Store,
  accountService: AccountService,
  cardManagementService: CardManagementService,
  transactionService: TransactionService,
  savingsAccountService: SavingsAccountService,
  exchangeService: ExchangeService,
  confirmationRunner: ConfirmationRunner = createModalOutletConfirmationRunner(),
  // Optional so hosts that grant no document operations need not wire it;
  // documents.download fails fast with a clear message when it is absent.
  downloadDocumentService?: DownloadDocumentService,
  // The onboarding operations, built by custom-widget-onboarding.ts because they
  // share state with the onboardingStatus topic (verification markers, whether a
  // submit happened) that a per-call wrapper could not. Optional so a host that
  // grants no onboarding operation need not construct it; the operations then
  // fall through to 'unknown-operation', which is what an ungranted widget would
  // see anyway.
  onboarding?: Pick<ReturnType<typeof buildCustomWidgetOnboardingBridge>, 'handles' | 'call'>,
  // The session operations, built by custom-widget-auth.ts and shared with the
  // onboarding bridge (whose submit `onSuccess` performs the same sign-in). Also
  // optional: a host that grants no auth operation need not construct it, and the
  // operations then fall through to 'unknown-operation'.
  auth?: Pick<CustomWidgetAuthBridge, 'handles' | 'call'>,
  lookup?: Pick<ReturnType<typeof buildCustomWidgetLookupOperations>, 'handles' | 'call'>,
  // profile.update only. Optional for the same reason downloadDocumentService is: a host that
  // grants no profile write need not wire it, and the operation then fails fast saying so
  // rather than pretending to have written something.
  metadataService?: Pick<MetadataService, 'updateUserProfileData'>,
  // The customer's stored documents (documents.list/upload/delete, and the two stored
  // download kinds). Optional on the same terms as the services above.
  documentService?: Pick<
    DocumentService,
    'getDocumentFiles' | 'getDocumentFileId' | 'uploadDocument' | 'deleteStoredDocument' | 'downloadDocument'
  >,
  // notifications.list. The feed is the browser's own Novu session, which this service owns.
  notificationService?: Pick<NotificationService, 'callNovuServiceToFetchNotifications'>,
  // profile.changePassword. org-metadata owns the current-password proof and the realm policy.
  passwordService?: Pick<PasswordService, 'resetPassword'>,
): NonNullable<WidgetHostHandlers['api']> {
  async function listAccounts(
    payload: WidgetApiOperations['accounts.list']['payload'],
  ): Promise<WidgetPage<WidgetAccount>> {
    const response = await lastValueFrom(
      accountService.getAccounts({
        page: Number(payload.page ?? 1),
        size: Number(payload.size ?? 50),
        status: [AccountStatusType.active],
      }),
    );
    return {
      items: (response?.result ?? []).map(toWidgetAccount),
      page: response?.page,
      totalItems: response?.totalItems,
    };
  }

  async function listTransactions(
    payload: WidgetApiOperations['transactions.list']['payload'],
  ): Promise<WidgetPage<WidgetTransaction>> {
    const accountId = payload.accountId || store.selectSnapshot(AccountState.getCurrentAccountId);
    if (!accountId) throw new Error('transactions.list requires accountId (no account selected)');
    // from/to/sort ride through only when the widget sent them
    // (buildQueryString drops undefined), so an unfiltered call is exactly what
    // it was before they existed. Direction is deliberately not threadable —
    // the native seam hardcodes TRANSACTIONTYPEREQUEST per endpoint.
    const response = await lastValueFrom(
      accountService.getTransactionSummary(accountId, {
        page: Number(payload.page ?? 1),
        size: Number(payload.size ?? 10),
        dateFrom: payload.from,
        dateTo: payload.to,
        sort: payload.sort,
        type: TransactionType.ALL,
      }),
    );
    return {
      // type ALL responses carry TransactionInfoType items only
      items: ((response?.result ?? []) as TransactionWireItem[]).map(toWidgetTransaction),
      page: response?.page,
      totalItems: response?.totalItems,
    };
  }

  async function initiatePayment(
    // Partial: the payload crosses the frame boundary, so required fields are
    // re-validated at runtime before the request is built.
    payload: Partial<PaymentInitiationRequest>,
  ): Promise<PaymentInitiationResult> {
    const { sourceAccountId, beneficiaryId, amount, currency, reference, transferReasonId, scheduledAt } =
      payload;
    if (!sourceAccountId || !beneficiaryId || amount === undefined || amount === null) {
      throw new Error('payments.initiate requires sourceAccountId, beneficiaryId and amount');
    }
    const amountNumber = Number(amount);
    if (Number.isNaN(amountNumber)) throw new Error('payments.initiate amount must be numeric');

    // TransferMoneyRequest omits currency but the backend TransferMoneyDTO
    // accepts it as an optional field.
    const request: TransferMoneyRequest & { currency?: string } = {
      sourceAccountId,
      bid: beneficiaryId,
      amount: amountNumber,
      paymentReference: reference ?? '',
      transferReasonId: transferReasonId ?? '',
    };
    if (currency) request.currency = currency;
    if (scheduledAt) {
      // POST /payments validates schedulePaymentDate as nothing more than an
      // optional string, so the native flow's own client-side rule
      // (isValidScheduleDate: 'YYYY-MM-DD', today or later) is enforced here —
      // shared with the app host and the mock via the SDK helper.
      const scheduleProblem = invalidScheduledAtReason(String(scheduledAt));
      if (scheduleProblem) throw new Error(`payments.initiate ${scheduleProblem}`);
      request.schedulePaymentDate = String(scheduledAt);
    }

    // Mirrors submitUnifiedPayment: non-GBP/EUR payments go through the
    // international path with review disabled so a single call executes.
    const accountCurrency =
      currency ??
      store
        .selectSnapshot(AccountState.getAccounts)
        ?.accounts?.find((account) => account.accountId === sourceAccountId)?.currency;
    if (accountCurrency && accountCurrency !== 'GBP' && accountCurrency !== 'EUR') {
      request.review = false;
    }

    const response = await lastValueFrom(accountService.transferMoney(request));
    // POST /payments answers with the same body for three different outcomes —
    // an executed transfer, a future-dated job, a cancellation-window job — so
    // the discriminant is derived from the body by the SDK's own helper rather
    // than by this host, which would otherwise drift from the app host.
    return {
      ...response,
      transactionId: response?.id,
      status: derivePaymentInitiationStatus(response),
    };
  }

  async function checkPayee(payload: CopCheckRequest): Promise<CopCheckResult> {
    if (!payload.name) throw new Error('beneficiaries.cop.check requires name');
    const response = await lastValueFrom(accountService.checkPayee(toPayee(payload)));
    return toCopCheckResult(response);
  }

  async function listTransferReasons(
    payload: WidgetApiOperations['payments.reasons.list']['payload'],
  ): Promise<WidgetTransferReason[]> {
    const accountId = payload.accountId || store.selectSnapshot(AccountState.getCurrentAccountId);
    if (!accountId) throw new Error('payments.reasons.list requires accountId (no account selected)');
    // Native parity: payment-reason-widget falls back to GB when the flow
    // carries no beneficiary country.
    const reasons = await lastValueFrom(
      accountService.getTransferReasons(accountId, payload.country || 'GB', payload.category),
    );
    return (reasons ?? []).map(toWidgetTransferReason);
  }

  async function getBeneficiaryRequirements(
    payload: Partial<WidgetApiOperations['beneficiaries.requirements']['payload']>,
  ): Promise<WidgetBeneficiaryRequirements> {
    if (!payload.currency) throw new Error('beneficiaries.requirements requires currency');
    const response = await lastValueFrom(
      accountService.getBeneficiaryRequirements(
        payload.country ?? '',
        payload.currency,
        payload.scheme as PaymentRailOptions | undefined,
      ),
    );
    return toWidgetBeneficiaryRequirements(response);
  }

  async function createBeneficiary(
    payload: Partial<BeneficiaryCreateRequest>,
    confirmation?: ConfirmationOutcome,
  ): Promise<BeneficiaryCreateResult> {
    if (!payload.name || !payload.currency) {
      throw new Error('beneficiaries.create requires name and currency');
    }
    // Native parity (callAddBeneficiaryAPI): the confirmation access token is
    // attached to the request; addBeneficiary sends it as the `confirmation`
    // header consumed by the endpoint's ConfirmationGuard.
    const response = await lastValueFrom(
      accountService.addBeneficiary(
        toAddBeneficiaryRequest(payload as BeneficiaryCreateRequest),
        confirmation?.token,
      ),
    );
    return { beneficiaryId: response?.id ?? '' };
  }

  async function listCards(
    payload: WidgetApiOperations['cards.list']['payload'],
  ): Promise<WidgetCard[]> {
    // Defaults mirror getCards() exactly (page 1, size 20, sort by createdDate
    // DESC); the filters only reach the wire when the widget sent them, so an
    // empty payload is byte-for-byte the request this operation always made.
    const filters: { name?: string; type?: 'virtual' | 'physical'; status?: string } = {};
    if (payload.name) filters.name = payload.name;
    if (payload.type) filters.type = payload.type;
    if (payload.status) filters.status = payload.status;
    const response = await lastValueFrom(
      cardManagementService.getCards(
        Number(payload.page ?? 1),
        Number(payload.size ?? 20),
        undefined,
        undefined,
        filters,
      ),
    );
    return (response?.result ?? []).map(toWidgetCard);
  }

  async function listBeneficiaries(
    payload: WidgetApiOperations['beneficiaries.list']['payload'],
  ): Promise<WidgetPage<WidgetBeneficiary>> {
    // The plain native GET /beneficiaries listing; omitted params stay off the
    // wire and the backend's own defaults apply (page 1, size 20). Rows come
    // from the platform table the beneficiaries topic reads — provider-synced
    // out-of-band.
    const response = await lastValueFrom(
      accountService.listBeneficiaries({
        page: payload.page !== undefined ? Number(payload.page) : undefined,
        size: payload.size !== undefined ? Number(payload.size) : undefined,
        name: payload.name,
        currency: payload.currency,
        country: payload.country,
        accountNumber: payload.accountNumber,
      }),
    );
    return {
      items: (response?.result ?? []).map(toWidgetBeneficiary),
      page: response?.page,
      totalItems: response?.totalItems,
    };
  }

  async function getTransactionDetail(
    payload: Partial<WidgetApiOperations['transactions.detail']['payload']>,
  ): Promise<WidgetTransactionDetail> {
    if (!payload.transactionId) throw new Error('transactions.detail requires transactionId');
    // The wire endpoint keys the lookup by direction; anything else 404s or
    // belongs to a different endpoint (scheduled/exchange), so fail fast.
    const type = payload.type;
    if (type !== TransactionType.INCOMING && type !== TransactionType.OUTGOING) {
      throw new Error("transactions.detail requires type 'incoming' or 'outgoing'");
    }
    const accountId = payload.accountId || store.selectSnapshot(AccountState.getCurrentAccountId);
    if (!accountId) throw new Error('transactions.detail requires accountId (no account selected)');
    const response = await lastValueFrom(
      // First param is transactionNumber — the id lookup path passes ''.
      transactionService.getTransactionDetailById('', payload.transactionId, type, accountId),
    );
    return toWidgetTransactionDetail(response);
  }

  async function listScheduledPayments(
    payload: WidgetApiOperations['payments.scheduled.list']['payload'],
  ): Promise<WidgetPage<WidgetScheduledPayment>> {
    const response = await lastValueFrom(
      accountService.getCancellablePayments(Number(payload.page ?? 1), Number(payload.size ?? 10)),
    );
    return {
      items: (response?.result ?? []).map(toWidgetScheduledPayment),
      page: response?.page,
      totalItems: response?.totalItems,
    };
  }

  async function listPaymentRequests(
    payload: WidgetApiOperations['paymentRequests.list']['payload'],
  ): Promise<WidgetPage<WidgetPaymentRequest>> {
    const response = await lastValueFrom(
      accountService.getPaymentRequest(
        Number(payload.page ?? 1),
        Number(payload.size ?? 10),
        payload.status,
        payload.query,
      ),
    );
    return {
      items: (response?.result ?? []).map(toWidgetPaymentRequest),
      page: response?.page,
      totalItems: response?.totalItems,
    };
  }

  async function getPaymentRequestDetail(
    payload: Partial<WidgetApiOperations['paymentRequests.get']['payload']>,
  ): Promise<WidgetPaymentRequest> {
    if (!payload.paymentRequestId) throw new Error('paymentRequests.get requires paymentRequestId');
    const response = await lastValueFrom(
      accountService.getPaymentRequestDetailsById(payload.paymentRequestId),
    );
    return toWidgetPaymentRequestDetail(response);
  }

  async function getCardLimits(
    payload: Partial<WidgetApiOperations['cards.limits.get']['payload']>,
  ): Promise<WidgetCardLimit[]> {
    if (!payload.cardId) throw new Error('cards.limits.get requires cardId');
    const response = await lastValueFrom(cardManagementService.getCardLimits(payload.cardId));
    return (response ?? []).map(toWidgetCardLimit);
  }

  async function listSavingsAccounts(
    payload: WidgetApiOperations['savings.accounts.list']['payload'],
  ): Promise<WidgetSavingsAccount[]> {
    // Page/size default to 1/50 — the values this operation always sent.
    const response = await lastValueFrom(
      savingsAccountService.getSavingAccounts({
        page: Number(payload.page ?? 1),
        size: Number(payload.size ?? 50),
        status: [AccountStatusType.active],
      }),
    );
    return (response?.result ?? []).map(toWidgetSavingsAccount);
  }

  async function listSavingsProducts(
    payload: WidgetApiOperations['savings.products.list']['payload'],
  ): Promise<WidgetSavingsProduct[]> {
    // 'standard' sends no category param at all, like the native model-builder:
    // the backend's default IS the new-account catalogue and every provider
    // branches on `=== 'renewal'`, so an explicit new-account value would be one
    // no provider has ever been sent.
    const products = await lastValueFrom(
      payload?.category === 'renewal'
        ? savingsAccountService.getAvailableProducts({ category: 'renewal' })
        : savingsAccountService.getAvailableProducts(),
    );
    return (products ?? []).map(toWidgetSavingsProduct);
  }

  async function listCustomerSavingsProducts(): Promise<WidgetSavingsProduct[]> {
    // Org-scoped catalogue; the organisation comes from the session on the
    // backend, so there is nothing to pass and nothing to filter on.
    const products = await lastValueFrom(savingsAccountService.getCustomerAvailableProducts());
    return (products ?? []).map(toWidgetSavingsProduct);
  }

  async function createSavingsAccount(
    payload: Partial<SavingsAccountCreateRequest>,
  ): Promise<SavingsAccountCreateResult> {
    if (!payload.productCode || !payload.accountType || !payload.currency) {
      throw new Error('savings.create requires productCode, accountType and currency');
    }
    if (!SAVINGS_ACCOUNT_TYPES.includes(payload.accountType)) {
      throw new Error(`savings.create accountType must be one of ${SAVINGS_ACCOUNT_TYPES.join(', ')}`);
    }
    const response = await lastValueFrom(
      savingsAccountService.createSavingsAccount(
        toCreateSavingsAccountRequest(payload as SavingsAccountCreateRequest),
      ),
    );
    return toSavingsAccountCreateResult(response?.data);
  }

  async function renameSavingsAccount(
    payload: Partial<WidgetApiOperations['savings.accounts.rename']['payload']>,
  ): Promise<WidgetApiOperations['savings.accounts.rename']['result']> {
    if (!payload.savingsAccountId || !payload.accountName) {
      throw new Error('savings.accounts.rename requires savingsAccountId and accountName');
    }
    await lastValueFrom(
      savingsAccountService.updateSavingAccount(payload.savingsAccountId, { name: payload.accountName }),
    );
    // The route answers no usable body; the echoed request is the confirmation.
    return { savingsAccountId: payload.savingsAccountId, accountName: payload.accountName };
  }

  async function listSavingsAccountRequests(
    payload: WidgetApiOperations['savings.requests.list']['payload'],
  ): Promise<WidgetApiOperations['savings.requests.list']['result']> {
    // `undefined` is the unfiltered list, which is what an omitted status means
    // on the contract — the service's own default ('awaiting-approval') is for
    // the native widget, not for this operation.
    const requests = await lastValueFrom(savingsAccountService.getAccountRequests(payload?.status));
    return (requests ?? []).map(toWidgetSavingsAccountRequest);
  }

  /* ── the nominated account: where every savings payout goes ───────────── */

  async function getNominatedAccount(): Promise<WidgetNominatedAccount | null> {
    // The native GET 404s when none is set; "never linked one" is a state a widget renders,
    // not a failure, so the 404 becomes null here exactly as it does on the BFF.
    try {
      const details = await lastValueFrom(savingsAccountService.fetchNominatedAccountInformation(''));
      return details ? toWidgetNominatedAccount(details) : null;
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 404) return null;
      throw error;
    }
  }

  async function listNominatedAccountInstitutions(
    payload: WidgetApiOperations['savings.nominated.institutions']['payload'],
  ): Promise<WidgetNominatedAccountInstitution[]> {
    const countryCode = payload?.countryCode ? String(payload.countryCode).toUpperCase() : 'GB';
    const institutions = await lastValueFrom(
      savingsAccountService.fetchSupportedAispInstitutions(countryCode),
    );
    return (institutions ?? []).map((institution) => ({
      id: institution.id,
      name: institution.name,
      bic: institution.bic,
      ...(institution.countries ? { countries: institution.countries } : {}),
      logo: institution.logo,
    }));
  }

  async function connectNominatedAccount(
    payload: Partial<WidgetApiOperations['savings.nominated.connect']['payload']>,
  ): Promise<NominatedAccountConnectResult> {
    if (!payload?.institutionId || !payload?.redirectUrl) {
      throw new Error('savings.nominated.connect requires institutionId and redirectUrl');
    }
    const response = await lastValueFrom(
      savingsAccountService.connectNominatedAccount({
        institutionId: payload.institutionId,
        redirectUrl: payload.redirectUrl,
      }),
    );
    // `link` is the vendor's name for it; the contract calls it `url`.
    return { requestId: response.requestId, url: response.link };
  }

  async function processNominatedAccount(
    payload: Partial<WidgetApiOperations['savings.nominated.process']['payload']>,
  ): Promise<NominatedAccountProcessResult> {
    if (!payload?.requestId) {
      throw new Error('savings.nominated.process requires requestId');
    }
    const response = await lastValueFrom(
      savingsAccountService.processNominatedAccount(payload.requestId, payload.accountId),
    );
    return {
      status: response.status as NominatedAccountProcessResult['status'],
      ...(response.accountIds ? { accountIds: response.accountIds } : {}),
    };
  }

  // There is no `savings.nominated.update` here, and its absence is a decision (2026-08-27):
  // a nominated account is set only through institutions → connect → process, and changing or
  // unsetting a connected one is a bank-side support action. `SavingsAccountService`'s own
  // `updateNominatedAccount` remains for the platform's native screens; no widget reaches it.

  /**
   * The customer's own details. Same route the native edit-profile CTA uses
   * (`PATCH {metadata}/users`), and the same gates behind it: the project's permitted-field
   * policy, the contact-change cooldown and the OTP verification of a new email or phone are
   * all enforced server-side, so a refusal is surfaced rather than pre-empted.
   *
   * `emailVerified` / `phoneVerified` are deliberately NOT sent, unlike the legacy CTA path
   * that asserts them from form state: those are the very claims the server OTP gate exists
   * to make.
   */
  async function updateProfile(
    payload: Partial<WidgetApiOperations['profile.update']['payload']>,
  ): Promise<WidgetProfile> {
    if (!metadataService) {
      throw new Error('profile.update is not available: this host has no metadata service wired');
    }
    const wire = toProfileUpdatePayload(payload ?? {});
    if (Object.keys(wire).length === 0) {
      throw new Error('profile.update requires at least one field to change');
    }
    const updated = await lastValueFrom(metadataService.updateUserProfileData(wire));
    // The route answers the attribute payload it wrote, which carries no id and no active
    // role — both belong to the session, not to the edit — so they come from the profile
    // state the `profile` topic already reads.
    const current = store.selectSnapshot(ProfileState.getProfile);
    return {
      id: current?.id ?? '',
      firstName: updated?.firstName ?? current?.firstName,
      lastName: updated?.lastName ?? current?.lastName,
      email: updated?.email ?? current?.email,
      activeRole: current?.activeRole,
    };
  }

  async function closeSavingsAccount(
    payload: Partial<SavingsAccountCloseRequest>,
  ): Promise<SavingsAccountCloseResult> {
    if (!payload.savingsAccountId) throw new Error('savings.close requires savingsAccountId');
    await lastValueFrom(
      savingsAccountService.closeSavingsAccount(payload.savingsAccountId, {
        closureReason: payload.closureReason,
      }),
    );
    // The endpoint answers with no body, so a resolved promise is the outcome.
    return { savingsAccountId: payload.savingsAccountId };
  }

  async function transferBetweenSavingsAccounts(
    payload: Partial<SavingsInternalTransferRequest>,
  ): Promise<SavingsInternalTransferResult> {
    const { fromSavingsAccountId, toSavingsAccountId, amount } = payload;
    if (!fromSavingsAccountId || !toSavingsAccountId || amount === undefined || amount === null) {
      throw new Error(
        'savings.internalTransfer requires fromSavingsAccountId, toSavingsAccountId and amount',
      );
    }
    const amountNumber = Number(amount);
    if (Number.isNaN(amountNumber)) throw new Error('savings.internalTransfer amount must be numeric');
    await lastValueFrom(
      savingsAccountService.initiateInternalTransfer(fromSavingsAccountId, {
        accountId: toSavingsAccountId,
        amount: amountNumber,
      }),
    );
    return { fromSavingsAccountId, toSavingsAccountId, amount: amountNumber };
  }

  async function withdrawFromSavingsAccount(
    payload: Partial<SavingsWithdrawRequest>,
  ): Promise<SavingsWithdrawResult> {
    const { savingsAccountId, amount } = payload;
    if (!savingsAccountId || amount === undefined || amount === null) {
      throw new Error('savings.withdraw requires savingsAccountId and amount');
    }
    const amountNumber = Number(amount);
    if (Number.isNaN(amountNumber)) throw new Error('savings.withdraw amount must be numeric');
    // The account is read first for two things the native flow gets from page
    // state: the availableBalance the amount is validated against (native
    // parity — the withdrawal util blocks amounts above it before any HTTP
    // call), and the accountType the outcome is derived from, because the
    // response body is unusable (see SavingsWithdrawResult).
    const account = await lastValueFrom(
      savingsAccountService.getActiveSavingAccountDetails(savingsAccountId),
    );
    const availableBalance = Number(account?.availableBalance);
    if (Number.isFinite(availableBalance) && amountNumber > availableBalance) {
      throw new Error('savings.withdraw amount is more than the available balance');
    }
    await lastValueFrom(
      savingsAccountService.withdrawFromSavingsAccount(savingsAccountId, {
        amount: amountNumber,
        description: payload.description ? String(payload.description) : undefined,
      }),
    );
    return {
      savingsAccountId,
      amount: amountNumber,
      outcome: account?.accountType === 'notice' ? 'notice-submitted' : 'withdrawn',
    };
  }

  /**
   * Builds the wire MaturityDecisionRequest from the contract's discriminant,
   * mirroring the three native CTA flows: 'renew' and 'withdraw' send the
   * legacy `{ decision, productCode? }` shape, 'partial-reinvest' sends
   * `advanced.strategy: 'CUSTOM'` with explicit REINVEST/WITHDRAWAL legs
   * computed from the account's current balance — legs of zero or less are
   * omitted and an over-balance reinvest is not capped, exactly as the native
   * calculation behaves.
   */
  async function setMaturityDecision(
    payload: Partial<SavingsMaturitySetRequest>,
  ): Promise<SavingsMaturitySetResult> {
    const { savingsAccountId, decision, productCode, reinvestAmount } = payload;
    if (!savingsAccountId) throw new Error('savings.maturity.set requires savingsAccountId');
    if (decision !== 'renew' && decision !== 'withdraw' && decision !== 'partial-reinvest') {
      throw new Error("savings.maturity.set decision must be 'renew', 'withdraw' or 'partial-reinvest'");
    }
    if ((decision === 'renew' || decision === 'partial-reinvest') && !productCode) {
      throw new Error(`savings.maturity.set '${decision}' requires productCode`);
    }

    let request: MaturityDecisionRequest;
    if (decision === 'renew') {
      request = { decision: MaturityDecision.RENEW, productCode };
    } else if (decision === 'withdraw') {
      request = { decision: MaturityDecision.WITHDRAW };
    } else {
      const reinvestNumber = Number(reinvestAmount);
      if (!Number.isFinite(reinvestNumber) || reinvestNumber <= 0) {
        throw new Error("savings.maturity.set 'partial-reinvest' requires a positive reinvestAmount");
      }
      // The native partial-reinvest flow reads balance and currency from the
      // active account's state; the bridge reads the same account fresh.
      const account = await lastValueFrom(
        savingsAccountService.getActiveSavingAccountDetails(savingsAccountId),
      );
      const balance = Number(account?.balance);
      const currency = account?.currency ?? '';
      const withdrawalNumber = balance - reinvestNumber;
      const actions: NonNullable<MaturityDecisionRequest['advanced']>['actions'] = [
        {
          type: 'REINVEST',
          amount: { amount: reinvestNumber.toString(), currency },
          productCode,
        },
      ];
      if (withdrawalNumber > 0) {
        actions.push({
          type: 'WITHDRAWAL',
          amount: { amount: withdrawalNumber.toString(), currency },
        });
      }
      request = { advanced: { strategy: 'CUSTOM', actions } };
    }

    await lastValueFrom(savingsAccountService.submitMaturityDecision(request, savingsAccountId));
    // The endpoint answers an empty data object the native flow never reads;
    // the echoed instruction is the confirmation of what was recorded.
    return { savingsAccountId, decision };
  }

  async function quoteExchange(payload: Partial<ExchangeQuoteRequest>): Promise<ExchangeQuoteResult> {
    const { buyAccountId, sellAccountId, amount, fixedSide } = payload;
    if (!buyAccountId || !sellAccountId || amount === undefined || amount === null) {
      throw new Error('exchange.quote requires buyAccountId, sellAccountId and amount');
    }
    if (fixedSide !== 'buy' && fixedSide !== 'sell') {
      throw new Error("exchange.quote requires fixedSide 'buy' or 'sell'");
    }
    const amountNumber = Number(amount);
    if (Number.isNaN(amountNumber)) throw new Error('exchange.quote amount must be numeric');
    const response = await lastValueFrom(
      exchangeService.postExchangeQuote(
        toExchangeQuotePayload({ ...(payload as ExchangeQuoteRequest), amount: amountNumber }),
      ),
    );
    return toExchangeQuoteResult(response);
  }

  async function acceptExchangeQuote(
    payload: Partial<WidgetApiOperations['exchange.accept']['payload']>,
  ): Promise<ExchangeAcceptResult> {
    if (!payload.quoteId) throw new Error('exchange.accept requires quoteId');
    const response = await lastValueFrom(exchangeService.postAcceptQuote(payload.quoteId));
    // postAcceptQuote swallows HTTP failures into null, so the widget would
    // otherwise see a resolved call for a trade that never happened.
    if (!response) throw new Error(`exchange.accept failed for quote ${payload.quoteId}`);
    return toExchangeAcceptResult(response);
  }

  async function cancelScheduledPayment(
    payload: Partial<WidgetApiOperations['payments.scheduled.cancel']['payload']>,
  ): Promise<{ paymentId: string }> {
    if (!payload.paymentId) throw new Error('payments.scheduled.cancel requires paymentId');
    await lastValueFrom(accountService.removeScheduledPayment(payload.paymentId));
    return { paymentId: payload.paymentId };
  }

  async function createPaymentRequest(
    payload: Partial<PaymentRequestCreateRequest>,
  ): Promise<PaymentRequestCreateResult> {
    const { beneficiaryId, amount, currency, reference, transferReasonId } = payload;
    if (!beneficiaryId || amount === undefined || amount === null || !currency) {
      throw new Error('paymentRequests.create requires beneficiaryId, amount and currency');
    }
    const amountNumber = Number(amount);
    if (Number.isNaN(amountNumber)) throw new Error('paymentRequests.create amount must be numeric');
    // Unlike POST /payments (whose DTO relaxed these per-provider),
    // PaymentsRequestDTO is unconditional: paymentReference, transferReasonId
    // and transferReasonIdText are all @IsNotEmpty. Reject here, before any
    // HTTP, rather than let the route 400 on a body the contract could never
    // satisfy — the natively working flow (paymentRequestProcessing) sends all
    // three from the form.
    if (!reference) {
      throw new Error(
        'paymentRequests.create requires reference (POST /payments/request rejects an empty paymentReference)',
      );
    }
    if (!transferReasonId) {
      throw new Error(
        'paymentRequests.create requires transferReasonId — pick one via payments.reasons.list ' +
          '(POST /payments/request rejects a request without a transfer reason)',
      );
    }
    // Native parity (paymentRequestProcessing): the request is raised against
    // the currently selected account unless the widget names one.
    const sourceAccountId =
      payload.sourceAccountId || store.selectSnapshot(AccountState.getCurrentAccountId);
    if (!sourceAccountId) {
      throw new Error('paymentRequests.create requires sourceAccountId (no account selected)');
    }
    // The DTO also demands the reason's human-readable label
    // (transferReasonIdText), which the contract deliberately does not carry:
    // it is display data, resolved from the same catalogue
    // payments.reasons.list serves — native parity with the payment-reason
    // widget, which writes the picked reason's description alongside its id
    // (and falls back to GB when the flow carries no beneficiary country).
    const reasons = await lastValueFrom(accountService.getTransferReasons(sourceAccountId, 'GB'));
    const reason = (reasons ?? []).find((entry) => entry.id === transferReasonId);
    if (!reason) {
      throw new Error(
        `paymentRequests.create: transferReasonId '${transferReasonId}' is not in the account's ` +
          "transfer-reason catalogue (payments.reasons.list)",
      );
    }
    const response = await lastValueFrom(
      accountService.makePaymentRequest(
        toPaymentRequestorRequest(
          { ...(payload as PaymentRequestCreateRequest), amount: amountNumber },
          sourceAccountId,
          reason.description,
        ),
      ),
    );
    return { paymentRequestId: response?.id ?? '' };
  }

  async function createVirtualCard(payload: Partial<CardCreateRequest>): Promise<CardCreateResult> {
    if (!payload.accountId) throw new Error('cards.create requires accountId');
    // POST /cards (CreateCardRequest) demands a non-empty cardReferenceType —
    // the card-product key from the project's cards configuration
    // (cardsConfig.cardOptions), which the provider adapters resolve to their
    // product/design attributes. That configuration is provider-details data
    // this host cannot read, so there is no derivable default: the widget must
    // name the product, exactly as the native add-card form does.
    if (!payload.cardReferenceType) {
      throw new Error(
        'cards.create requires cardReferenceType (the card-product key from the project cards ' +
          'configuration — POST /cards rejects a body without one)',
      );
    }
    // Native parity (addCard, add-card.utils.ts): the working native flow posts
    // { accountIds, type, cardReferenceType } through addCard. The legacy
    // createVirtualCard service method sends { accountId, cardType }, which the
    // CreateCardRequest DTO rejects — it is dead natively (nothing dispatches
    // MakeVirtualCard) and is deliberately not used here. The response body is
    // discarded (the accountId given is echoed back) — the new card is only
    // discoverable through cards.list.
    await lastValueFrom(
      cardManagementService.addCard({
        accountIds: [payload.accountId],
        type: CardType.VIRTUAL,
        cardReferenceType: payload.cardReferenceType,
      }),
    );
    return { accountId: payload.accountId };
  }

  async function createInternationalBeneficiary(
    payload: Partial<InternationalBeneficiaryCreateRequest>,
    confirmation?: ConfirmationOutcome,
  ): Promise<BeneficiaryCreateResult> {
    if (!payload.name || !payload.currency) {
      throw new Error('beneficiaries.createInternational requires name and currency');
    }
    // Native parity (callAddBeneficiaryAPI): the confirmation access token is
    // attached, and addBeneficiary sends it as the `confirmation` header the
    // endpoint's ConfirmationGuard reads.
    const response = await lastValueFrom(
      accountService.addBeneficiary(
        toAddInternationalBeneficiaryRequest(payload as InternationalBeneficiaryCreateRequest),
        confirmation?.token,
      ),
    );
    return { beneficiaryId: response?.id ?? '' };
  }

  /** Shared validation for the two international payment operations. */
  function toValidatedInternationalPayment(
    operation: string,
    payload: Partial<InternationalPaymentRequest>,
    review: boolean,
  ): TransferMoneyRequest {
    const { sourceAccountId, beneficiaryId, amount } = payload;
    if (!sourceAccountId || !beneficiaryId || amount === undefined || amount === null) {
      throw new Error(`${operation} requires sourceAccountId, beneficiaryId and amount`);
    }
    const amountNumber = Number(amount);
    if (Number.isNaN(amountNumber)) throw new Error(`${operation} amount must be numeric`);
    return toInternationalTransferMoneyRequest(
      { ...(payload as InternationalPaymentRequest), amount: amountNumber },
      review,
    );
  }

  async function precheckInternationalPayment(
    payload: Partial<InternationalPaymentRequest>,
  ): Promise<InternationalPaymentPrecheckResult> {
    const request = toValidatedInternationalPayment('payments.precheckInternational', payload, true);
    const response = await lastValueFrom(accountService.transferMoneyInternational(request));
    return toInternationalPaymentPrecheckResult(response);
  }

  async function initiateInternationalPayment(
    payload: Partial<InternationalPaymentRequest>,
  ): Promise<InternationalPaymentResult> {
    const request = toValidatedInternationalPayment('payments.initiateInternational', payload, false);
    const response = await lastValueFrom(accountService.transferMoneyInternational(request));
    return toInternationalPaymentResult(response);
  }

  /**
   * The Flutter webview bridge, when the app is running inside the native
   * shell: DownloadDocumentService then hands the document URL to the host app
   * instead of fetching a blob (mirrors download-document.utils.ts). It never
   * goes to the widget either way.
   */
  function resolveFlutterWebView(): unknown {
    return (window as unknown as Record<string, unknown>)['flutter_inappwebview'] || undefined;
  }

  function toDocumentFormat(format: WidgetDocumentFormat | undefined): 'pdf' | 'csv' {
    return format === 'csv' ? 'csv' : 'pdf';
  }

  async function resolveKeyProductInformationUrl(productCode: string): Promise<string> {
    const products = await lastValueFrom(savingsAccountService.getAvailableProducts());
    const product = (products ?? []).find((candidate) => candidate.productCode === productCode);
    const url = product?.documents?.keyProductInformationUrl;
    if (!url) {
      throw new Error(`documents.download: no key product information for product ${productCode}`);
    }
    return url;
  }

  /**
   * Host-mediated download. Every branch resolves { started: true } and nothing
   * else: an opaque-origin frame could not use a same-origin document URL, and
   * handing one over would upgrade a one-document grant into a reusable bearer
   * capability the widget could pass on. The bytes stay in the host.
   */
  async function downloadDocument(
    payload: Partial<WidgetDocumentRequest> & { kind?: string },
  ): Promise<WidgetDocumentDownloadResult> {
    if (!downloadDocumentService) {
      throw new Error('documents.download is not available on this host');
    }
    const flutterWebView = resolveFlutterWebView();
    const request = payload as WidgetDocumentRequest;
    switch (request.kind) {
      case 'bankStatement': {
        if (!request.from || !request.to) {
          throw new Error("documents.download 'bankStatement' requires from and to");
        }
        const accountId = request.accountId || store.selectSnapshot(AccountState.getCurrentAccountId);
        if (!accountId) {
          throw new Error("documents.download 'bankStatement' requires accountId (no account selected)");
        }
        const format = toDocumentFormat(request.format);
        // Native parity (triggerBankStatementDownload): the selected
        // multi-currency wallet code scopes the statement when there is one.
        const currency =
          request.currency ||
          store.selectSnapshot(AccountState.getCurrentMultiCurrencyCode) ||
          undefined;
        await lastValueFrom(
          downloadDocumentService.downloadBankDocument(
            accountId,
            format === 'csv'
              ? DownloadDocumentTypeKey.BANK_STATEMENT_CSV
              : DownloadDocumentTypeKey.BANK_STATEMENT_PDF,
            request.from,
            request.to,
            format,
            flutterWebView,
            currency,
          ),
        );
        return { started: true };
      }
      case 'savingsStatement': {
        if (!request.savingsAccountId || !request.from || !request.to) {
          throw new Error(
            "documents.download 'savingsStatement' requires savingsAccountId, from and to",
          );
        }
        const format = toDocumentFormat(request.format);
        await lastValueFrom(
          downloadDocumentService.downloadSavingsBankDocument(
            request.savingsAccountId,
            format === 'csv'
              ? DownloadDocumentTypeKey.SAVINGS_BANK_STATEMENT_CSV
              : DownloadDocumentTypeKey.SAVINGS_BANK_STATEMENT_PDF,
            request.from,
            request.to,
            format,
            flutterWebView,
          ),
        );
        return { started: true };
      }
      case 'holdingStatement': {
        if (!request.from || !request.to) {
          throw new Error("documents.download 'holdingStatement' requires from and to");
        }
        // Only a pdf template exists for holdings, so the format is fixed.
        await lastValueFrom(
          downloadDocumentService.downloadHoldingDocument(
            DownloadDocumentTypeKey.HOLDING_STATEMENT_PDF,
            request.from,
            request.to,
            'pdf',
            flutterWebView,
          ),
        );
        return { started: true };
      }
      case 'paymentProof': {
        if (!request.transactionId || !request.transactionType) {
          throw new Error(
            "documents.download 'paymentProof' requires transactionId and transactionType",
          );
        }
        const accountId = request.accountId || store.selectSnapshot(AccountState.getCurrentAccountId);
        if (!accountId) {
          throw new Error("documents.download 'paymentProof' requires accountId (no account selected)");
        }
        const format = toDocumentFormat(request.format);
        await lastValueFrom(
          downloadDocumentService.downloadPaymentProof(
            accountId,
            format === 'csv'
              ? DownloadDocumentTypeKey.PROOF_OF_PAYMENT_CSV
              : DownloadDocumentTypeKey.PROOF_OF_PAYMENT_PDF,
            request.transactionId,
            request.transactionType,
            format,
            flutterWebView,
          ),
        );
        return { started: true };
      }
      case 'exchangeProof': {
        if (!request.exchangeId) {
          throw new Error("documents.download 'exchangeProof' requires exchangeId");
        }
        const format = toDocumentFormat(request.format);
        await lastValueFrom(
          downloadDocumentService.downloadExchangeProof(
            format === 'csv'
              ? DownloadDocumentTypeKey.PROOF_OF_EXCHANGE_CSV
              : DownloadDocumentTypeKey.PROOF_OF_EXCHANGE_PDF,
            request.exchangeId,
            // Native parity: the transaction-history action sends '' here.
            request.transactionType ?? '',
            format,
            flutterWebView,
          ),
        );
        return { started: true };
      }
      case 'keyProductInformation': {
        if (!request.productCode) {
          throw new Error("documents.download 'keyProductInformation' requires productCode");
        }
        const url = await resolveKeyProductInformationUrl(request.productCode);
        await lastValueFrom(downloadDocumentService.downloadFromDirectUrl(url, 'pdf', flutterWebView));
        return { started: true };
      }
      case 'stored': {
        if (!request.documentId) throw new Error("documents.download 'stored' requires documentId");
        const service = requireDocumentService('documents.download');
        const type = toDocumentActionType('documents.download', request.context);
        const entityId = resolveDocumentEntityId(
          'documents.download',
          request.context,
          request.entityId,
        );
        const documents = await lastValueFrom(service.getDocumentFileId(type, entityId));
        const stored = (documents ?? []).find((candidate) => candidate.id === request.documentId);
        if (!stored) throw new Error(`documents.download: no document ${request.documentId}`);
        return downloadStoredDocument(
          type,
          entityId,
          stored.fileId,
          (stored.category as DocumentActionCategoryType) ?? DocumentActionCategoryType.RECEIPTS,
          stored.name,
        );
      }
      case 'transactionReceipt': {
        if (!request.transactionId) {
          throw new Error("documents.download 'transactionReceipt' requires transactionId");
        }
        const service = requireDocumentService('documents.download');
        const documents = await lastValueFrom(
          service.getDocumentFiles(
            DocumentActionType.TRANSACTIONS,
            request.transactionId,
            DocumentActionCategoryType.RECEIPTS,
          ),
        );
        const receipt = documents?.[0];
        if (!receipt) {
          throw new Error(`documents.download: no stored receipt for transaction ${request.transactionId}`);
        }
        return downloadStoredDocument(
          DocumentActionType.TRANSACTIONS,
          request.transactionId,
          receipt.fileId,
          DocumentActionCategoryType.RECEIPTS,
          receipt.name,
        );
      }
      default:
        throw new Error(`documents.download unknown kind: ${String(payload.kind)}`);
    }
  }

  /* ───────────────── the customer's stored documents ───────────────── */

  function requireDocumentService(operation: string): NonNullable<typeof documentService> {
    if (!documentService) {
      throw new Error(`${operation} is not available: this host has no document service wired`);
    }
    return documentService;
  }

  /**
   * The contract's `context` as the platform's route segment, and its `category` as the
   * platform's category — the same two vocabularies the BFF maps, mapped the same way. An
   * unknown context is refused rather than defaulted: it names someone else's documents.
   */
  function toDocumentActionType(operation: string, context: unknown): DocumentActionType {
    switch (context) {
      case 'savings-account':
        return DocumentActionType.SAVING_ACCOUNTS;
      case 'current-account':
        return DocumentActionType.CURRENT_ACCOUNTS;
      case 'customer':
        return DocumentActionType.CUSTOMER_DOCUMENTS;
      case 'transaction':
        return DocumentActionType.TRANSACTIONS;
      default:
        throw new Error(`${operation} requires a valid context`);
    }
  }

  /**
   * The entity the documents hang off. `customer` resolves to the SIGNED-IN user from host
   * state, never from the payload — a widget must not be able to read another customer's file
   * by naming them.
   */
  function resolveDocumentEntityId(operation: string, context: unknown, entityId?: string): string {
    if (context === 'customer') {
      const userId = store.selectSnapshot(ProfileState.getProfile)?.id;
      if (!userId) throw new Error(`${operation} requires a signed-in customer`);
      return userId;
    }
    if (!entityId) throw new Error(`${operation} requires entityId for context ${String(context)}`);
    return entityId;
  }

  async function listDocuments(
    payload: Partial<WidgetApiOperations['documents.list']['payload']>,
  ): Promise<WidgetStoredDocument[]> {
    const service = requireDocumentService('documents.list');
    const type = toDocumentActionType('documents.list', payload?.context);
    const entityId = resolveDocumentEntityId('documents.list', payload?.context, payload?.entityId);
    const documents = await lastValueFrom(
      payload?.category
        ? service.getDocumentFiles(type, entityId, payload.category as DocumentActionCategoryType)
        : service.getDocumentFileId(type, entityId),
    );
    return (documents ?? []).map(toWidgetStoredDocument);
  }

  async function uploadDocument(
    payload: Partial<WidgetApiOperations['documents.upload']['payload']>,
  ): Promise<{ documentId: string }> {
    const service = requireDocumentService('documents.upload');
    const type = toDocumentActionType('documents.upload', payload?.context);
    const entityId = resolveDocumentEntityId('documents.upload', payload?.context, payload?.entityId);
    if (!payload?.fileName || !payload?.contentType || !payload?.contentBase64) {
      throw new Error('documents.upload requires fileName, contentType and contentBase64');
    }
    if (!payload?.category) throw new Error('documents.upload requires category');
    const form = new FormData();
    form.append('files', base64ToBlob(payload.contentBase64, payload.contentType), payload.fileName);
    form.append('entityId', entityId);
    form.append('type', type);
    form.append('category', payload.category);
    const documents = await lastValueFrom(service.uploadDocument(form));
    const documentId = documents?.[0]?.id;
    if (!documentId) throw new Error('documents.upload stored no document');
    return { documentId };
  }

  async function deleteDocument(
    payload: Partial<WidgetApiOperations['documents.delete']['payload']>,
  ): Promise<{ deleted: boolean }> {
    const service = requireDocumentService('documents.delete');
    if (!payload?.documentId) throw new Error('documents.delete requires documentId');
    const type = toDocumentActionType('documents.delete', payload?.context);
    const entityId = resolveDocumentEntityId('documents.delete', payload?.context, payload?.entityId);
    // The contract hands out the document ROW id; the route deletes by file id, so the row is
    // resolved through the same listing read — which is also what scopes it to this customer.
    const documents = await lastValueFrom(service.getDocumentFileId(type, entityId));
    const document = (documents ?? []).find((candidate) => candidate.id === payload.documentId);
    if (!document) return { deleted: false };
    const result = await lastValueFrom(
      service.deleteStoredDocument(type, entityId, {
        fileId: document.fileId,
        category: document.category as DocumentActionCategoryType | undefined,
      }),
    );
    return { deleted: result?.success === true };
  }

  /** base64 payload -> the Blob a multipart upload wants. */
  function base64ToBlob(contentBase64: string, contentType: string): Blob {
    const binary = atob(contentBase64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: contentType });
  }

  /**
   * The two STORED download kinds. Unlike the generated statements above, these stream a
   * document the platform already holds, so the host fetches the blob and hands it to the
   * browser's save flow — and still resolves { started: true } with no bytes and no URL.
   */
  async function downloadStoredDocument(
    type: DocumentActionType,
    entityId: string,
    fileId: string,
    category: DocumentActionCategoryType,
    name: string,
  ): Promise<WidgetDocumentDownloadResult> {
    const service = requireDocumentService('documents.download');
    const response = await lastValueFrom(service.downloadDocument(type, entityId, { category, fileId }));
    const blob = response?.body;
    if (!blob) throw new Error('documents.download returned no document');
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
    return { started: true };
  }

  /* ────────────────────── the in-app notification feed ────────────────────── */

  async function listNotifications(
    payload: Partial<WidgetApiOperations['notifications.list']['payload']>,
  ): Promise<WidgetPage<WidgetNotification>> {
    if (!notificationService) {
      throw new Error('notifications.list is not available: this host has no notification service wired');
    }
    const page = Number(payload?.page ?? 1);
    const size = Number(payload?.size ?? 10);
    const response = await notificationService.callNovuServiceToFetchNotifications(page, size);
    if (response?.error) throw new Error(`notifications.list failed: ${response.error.message}`);
    const items = (response?.data?.notifications ?? []).map(
      (notification): WidgetNotification => {
        const mapped: WidgetNotification = {
          id: String(notification.id),
          // Native parity (notifications.state.ts): the bell renders the subject and falls
          // back to the body when a workflow published none.
          subject: notification.subject?.length ? notification.subject : notification.body ?? '',
          read: notification.isRead === true,
          createdAt: notification.createdAt ?? '',
        };
        if (notification.body) mapped.body = notification.body;
        return mapped;
      },
    );
    return { items, page };
  }

  /* ──────────────────────── the customer's password ──────────────────────── */

  async function changePassword(
    payload: Partial<WidgetApiOperations['profile.changePassword']['payload']>,
  ): Promise<{ changed: true }> {
    if (!passwordService) {
      throw new Error('profile.changePassword is not available: this host has no password service wired');
    }
    if (!payload?.currentPassword || !payload?.newPassword) {
      throw new Error('profile.changePassword requires currentPassword and newPassword');
    }
    // confirmPassword is the wire DTO's typo guard; an application that has a two-box form has
    // already compared them, so the new password is sent for both.
    await lastValueFrom(
      passwordService.resetPassword(payload.currentPassword, payload.newPassword, payload.newPassword),
    );
    return { changed: true };
  }

  async function listBulkPayments(
    payload: WidgetApiOperations['bulkPayments.list']['payload'],
  ): Promise<WidgetPage<WidgetBulkPayment>> {
    const response = await lastValueFrom(
      // The URL is string-interpolated by the service, so an absent query must
      // become '' rather than reaching the wire as 'undefined'.
      accountService.getBulkPaymentHistory(
        Number(payload.page ?? 1),
        Number(payload.size ?? 10),
        payload.query ?? '',
      ),
    );
    return {
      items: (response?.result ?? []).map(toWidgetBulkPayment),
      page: response?.page,
      totalItems: response?.totalItems,
    };
  }

  async function getBulkPayment(
    payload: Partial<WidgetApiOperations['bulkPayments.get']['payload']>,
  ): Promise<WidgetBulkPaymentDetail> {
    if (!payload.bulkPaymentId) throw new Error('bulkPayments.get requires bulkPaymentId');
    const response = await lastValueFrom(
      accountService.getBulkPaymentById(
        Number(payload.page ?? 1),
        Number(payload.size ?? 10),
        payload.bulkPaymentId,
        payload.query ?? '',
      ),
    );
    const detail: WidgetBulkPaymentDetail = {
      bulkPaymentId: payload.bulkPaymentId,
      items: (response?.result ?? []).map(toWidgetBulkPaymentTransfer),
      page: response?.page,
      totalItems: response?.totalItems,
    };
    const summary = response?.meta?.bulkPaymentsSummary;
    if (summary) detail.summary = toWidgetBulkPaymentSummary(summary);
    return detail;
  }

  async function retryBulkPaymentTransfer(
    payload: Partial<WidgetApiOperations['bulkPayments.retry']['payload']>,
  ): Promise<{ bulkPaymentId: string; transferId: string }> {
    const { bulkPaymentId, transferId } = payload;
    if (!bulkPaymentId || !transferId) {
      throw new Error('bulkPayments.retry requires bulkPaymentId and transferId');
    }
    await lastValueFrom(accountService.retryBulkPaymentTransfer(bulkPaymentId, transferId));
    return { bulkPaymentId, transferId };
  }

  async function listPaymentSchemes(
    payload: WidgetApiOperations['payments.schemes.list']['payload'],
  ): Promise<WidgetPaymentScheme[]> {
    const schemes = await lastValueFrom(
      accountService.getPaymentSchemes(payload.country, payload.currency),
    );
    return (schemes ?? []).map(toWidgetPaymentScheme);
  }

  async function setCardFrozen(
    operation: 'cards.freeze' | 'cards.unfreeze',
    payload: Partial<WidgetApiOperations['cards.freeze']['payload']>,
  ): Promise<{ status: string }> {
    if (!payload.cardId) throw new Error(`${operation} requires cardId`);
    const request = { cardId: payload.cardId };
    const response = await lastValueFrom(
      operation === 'cards.freeze'
        ? cardManagementService.freezeCard(request)
        : cardManagementService.unfreezeCard(request),
    );
    return { status: response?.data?.status ?? '' };
  }


  return {
    call: async (operation: string, payload: Record<string, unknown>) => {
      const gate = CONFIRMATION_GATED_OPERATIONS[operation];
      let confirmation: ConfirmationOutcome | undefined;
      if (gate?.isConfirmationRequired(store)) {
        // Suspends the widget's api.call while the host runs its confirmation
        // UX; a rejection (confirmation-declined / unsupported) aborts before
        // any HTTP call. The outcome token is only threaded into operations
        // whose native flow attaches it: beneficiaries.create and
        // beneficiaries.createInternational send it in the `confirmation`
        // header (AccountService.addBeneficiary), while payments.initiate and
        // payments.initiateInternational attach nothing (native parity:
        // submitUnifiedPayment sequences POST /payments after
        // /confirmation-code and transferMoney sends no `confirmation`
        // header). The claim matches the endpoint's guard, so attaching the
        // token for payments becomes possible once the guard is strict.
        confirmation = await confirmationRunner.run(gate.claimType, operation);
      }
      // Onboarding first, and outside the switch: those operations are stateful
      // as a group (verification, whether a submit happened) and live with the
      // topic that reports the same state — see custom-widget-onboarding.ts.
      if (onboarding?.handles(operation)) return onboarding.call(operation, payload);
      // Same reasoning for the session operations, and they must be reachable
      // even on a host that wired no onboarding bridge.
      if (auth?.handles(operation)) return auth.call(operation, payload);
      if (lookup?.handles(operation)) return lookup.call(operation, payload);
      switch (operation) {
        case 'accounts.list':
          return listAccounts(payload as WidgetApiOperations['accounts.list']['payload']);
        case 'transactions.list':
          return listTransactions(payload as WidgetApiOperations['transactions.list']['payload']);
        case 'payments.initiate':
          return initiatePayment(payload as unknown as Partial<PaymentInitiationRequest>);
        case 'beneficiaries.cop.check':
          return checkPayee(payload as unknown as CopCheckRequest);
        case 'payments.reasons.list':
          return listTransferReasons(payload as WidgetApiOperations['payments.reasons.list']['payload']);
        case 'beneficiaries.requirements':
          return getBeneficiaryRequirements(
            payload as Partial<WidgetApiOperations['beneficiaries.requirements']['payload']>,
          );
        case 'beneficiaries.create':
          return createBeneficiary(payload as Partial<BeneficiaryCreateRequest>, confirmation);
        case 'cards.list':
          return listCards(payload as WidgetApiOperations['cards.list']['payload']);
        case 'beneficiaries.list':
          return listBeneficiaries(payload as WidgetApiOperations['beneficiaries.list']['payload']);
        case 'beneficiaries.select': {
          // The same dispatch the native beneficiary listings make on a row
          // click; downstream payment pages read the id back via
          // BeneficiaryState. Nothing leaves the app.
          const beneficiaryId = (payload as Partial<WidgetApiOperations['beneficiaries.select']['payload']>)
            .beneficiaryId;
          if (!beneficiaryId) throw new Error('beneficiaries.select requires beneficiaryId');
          store.dispatch(new SetSelectedBeneficiary(String(beneficiaryId)));
          return { selected: true };
        }
        case 'accounts.select': {
          // The same dispatch the native account listings make on a row click;
          // downstream widgets read it back via AccountState.getCurrentAccountId
          // (and getSelectedAccount). The state handler refuses ids it has not
          // listed, so an unknown accountId leaves the selection unchanged.
          // Nothing leaves the app.
          const accountId = (payload as Partial<WidgetApiOperations['accounts.select']['payload']>)
            .accountId;
          if (!accountId) throw new Error('accounts.select requires accountId');
          store.dispatch(new UpdateCurrentAccountId(String(accountId)));
          return { selected: true };
        }
        case 'accounts.selectCurrency': {
          // The same dispatch the native multi-currency widget makes on a
          // currency click; AccountState.getCurrentMultiCurrencyCode is what
          // downstream widgets (and this bridge's own payment defaults) read.
          // Nothing leaves the app.
          const currency = (
            payload as Partial<WidgetApiOperations['accounts.selectCurrency']['payload']>
          ).currency;
          if (!currency) throw new Error('accounts.selectCurrency requires currency');
          store.dispatch(new UpdateCurrentMultiCurrencyCode(String(currency)));
          return { selected: true };
        }
        case 'cards.select': {
          // The native cards widget dispatches the full card record on a card
          // click (UpdateCurrentCardDetail carries a CardInfoType), so resolve
          // the id against the cards the host has already listed for the page
          // — the same list the `cards` topic serves. The state handler then
          // refreshes the card's limits, exactly as it does natively.
          const cardId = (payload as Partial<WidgetApiOperations['cards.select']['payload']>).cardId;
          if (!cardId) throw new Error('cards.select requires cardId');
          const cardState = store.selectSnapshot(CardState.getCards) as CardStateModel | undefined;
          const card = cardState?.items?.find((item) => item.id === String(cardId));
          if (!card) {
            throw new Error(
              `cards.select: unknown cardId ${String(cardId)} — the card must be in the host's card list (the cards topic)`,
            );
          }
          store.dispatch(new UpdateCurrentCardDetail(card, String(cardId)));
          return { selected: true };
        }
        case 'savings.accounts.select': {
          // The same dispatch the native savings listings make on a row click;
          // savings pages read it back via SavingsAccountState. Like
          // beneficiaries.select, unknown ids are accepted and downstream
          // pages read an empty account. Nothing leaves the app.
          const savingsAccountId = (
            payload as Partial<WidgetApiOperations['savings.accounts.select']['payload']>
          ).savingsAccountId;
          if (!savingsAccountId) throw new Error('savings.accounts.select requires savingsAccountId');
          store.dispatch(new UpdateActiveSavingsAccountId(String(savingsAccountId)));
          return { selected: true };
        }
        case 'cards.freeze':
        case 'cards.unfreeze':
          return setCardFrozen(operation, payload as Partial<WidgetApiOperations['cards.freeze']['payload']>);
        case 'transactions.detail':
          return getTransactionDetail(
            payload as Partial<WidgetApiOperations['transactions.detail']['payload']>,
          );
        case 'payments.scheduled.list':
          return listScheduledPayments(
            payload as WidgetApiOperations['payments.scheduled.list']['payload'],
          );
        case 'paymentRequests.list':
          return listPaymentRequests(payload as WidgetApiOperations['paymentRequests.list']['payload']);
        case 'paymentRequests.get':
          return getPaymentRequestDetail(
            payload as Partial<WidgetApiOperations['paymentRequests.get']['payload']>,
          );
        case 'cards.limits.get':
          return getCardLimits(payload as Partial<WidgetApiOperations['cards.limits.get']['payload']>);
        case 'savings.accounts.list':
          return listSavingsAccounts(
            payload as WidgetApiOperations['savings.accounts.list']['payload'],
          );
        case 'savings.products.list':
          return listSavingsProducts(
            payload as WidgetApiOperations['savings.products.list']['payload'],
          );
        case 'savings.products.listForCustomer':
          return listCustomerSavingsProducts();
        case 'savings.create':
          return createSavingsAccount(payload as Partial<SavingsAccountCreateRequest>);
        case 'savings.close':
          return closeSavingsAccount(payload as Partial<SavingsAccountCloseRequest>);
        case 'savings.internalTransfer':
          return transferBetweenSavingsAccounts(payload as Partial<SavingsInternalTransferRequest>);
        case 'savings.withdraw':
          return withdrawFromSavingsAccount(payload as Partial<SavingsWithdrawRequest>);
        case 'savings.accounts.rename':
          return renameSavingsAccount(
            payload as Partial<WidgetApiOperations['savings.accounts.rename']['payload']>,
          );
        case 'savings.requests.list':
          return listSavingsAccountRequests(
            payload as WidgetApiOperations['savings.requests.list']['payload'],
          );
        case 'savings.maturity.set':
          return setMaturityDecision(payload as Partial<SavingsMaturitySetRequest>);
        case 'savings.nominated.get':
          return getNominatedAccount();
        case 'savings.nominated.institutions':
          return listNominatedAccountInstitutions(
            payload as WidgetApiOperations['savings.nominated.institutions']['payload'],
          );
        case 'savings.nominated.connect':
          return connectNominatedAccount(
            payload as Partial<WidgetApiOperations['savings.nominated.connect']['payload']>,
          );
        case 'savings.nominated.process':
          return processNominatedAccount(
            payload as Partial<WidgetApiOperations['savings.nominated.process']['payload']>,
          );
        case 'profile.update':
          return updateProfile(payload as Partial<WidgetApiOperations['profile.update']['payload']>);
        case 'exchange.quote':
          return quoteExchange(payload as Partial<ExchangeQuoteRequest>);
        case 'exchange.accept':
          return acceptExchangeQuote(
            payload as Partial<WidgetApiOperations['exchange.accept']['payload']>,
          );
        case 'payments.scheduled.cancel':
          return cancelScheduledPayment(
            payload as Partial<WidgetApiOperations['payments.scheduled.cancel']['payload']>,
          );
        case 'paymentRequests.create':
          return createPaymentRequest(payload as Partial<PaymentRequestCreateRequest>);
        case 'cards.create':
          return createVirtualCard(payload as Partial<CardCreateRequest>);
        case 'beneficiaries.createInternational':
          return createInternationalBeneficiary(
            payload as Partial<InternationalBeneficiaryCreateRequest>,
            confirmation,
          );
        case 'payments.precheckInternational':
          return precheckInternationalPayment(payload as Partial<InternationalPaymentRequest>);
        case 'payments.initiateInternational':
          return initiateInternationalPayment(payload as Partial<InternationalPaymentRequest>);
        case 'documents.download':
          return downloadDocument(payload as Partial<WidgetDocumentRequest> & { kind?: string });
        case 'documents.list':
          return listDocuments(payload as Partial<WidgetApiOperations['documents.list']['payload']>);
        case 'documents.upload':
          return uploadDocument(payload as Partial<WidgetApiOperations['documents.upload']['payload']>);
        case 'documents.delete':
          return deleteDocument(payload as Partial<WidgetApiOperations['documents.delete']['payload']>);
        case 'notifications.list':
          return listNotifications(
            payload as Partial<WidgetApiOperations['notifications.list']['payload']>,
          );
        // No 'notifications.markRead': the only mark-read route this host has marks a whole
        // channel and takes no message ids, so it could not honour half the payload. See
        // WIDGET_OPERATION_SUPPORT in the SDK for the full reason.
        case 'profile.changePassword':
          return changePassword(
            payload as Partial<WidgetApiOperations['profile.changePassword']['payload']>,
          );
        case 'bulkPayments.list':
          return listBulkPayments(payload as WidgetApiOperations['bulkPayments.list']['payload']);
        case 'bulkPayments.get':
          return getBulkPayment(payload as Partial<WidgetApiOperations['bulkPayments.get']['payload']>);
        case 'bulkPayments.retry':
          return retryBulkPaymentTransfer(
            payload as Partial<WidgetApiOperations['bulkPayments.retry']['payload']>,
          );
        case 'payments.schemes.list':
          return listPaymentSchemes(payload as WidgetApiOperations['payments.schemes.list']['payload']);
        case 'providerCapabilities.list':
          return lastValueFrom(accountService.listProviderCapabilities());
        case 'providerCapabilities.invoke':
          return lastValueFrom(
            accountService.invokeProviderCapability(
              String((payload as { capability?: string }).capability ?? ''),
              (payload as { payload?: Record<string, unknown> }).payload,
            ),
          );
        default:
          throw new Error(`unknown-operation: ${operation}`);
      }
    },
  };
}
